import { elements } from '@ecosystem/newron-design-system';
import React, { Fragment } from 'react';
import { MenuButtonProps, SelectionMenuComponent } from '../../common/selection.menu';
import { ContextManager } from '../../context/manager';
import { useMobile } from '../../context/mobile/mobile.provider';
import { PageTitleSelector, Params } from '../../types';
import { useCorePageState } from '../provider';
import './title.css';
import classNames from 'classnames';

type Props = {
  title: string | (string | PageTitleSelector)[];
  pageParams: Params;
  updateSettings: (updates: Params) => void;
};
function PageTitle({ title, pageParams, updateSettings }: Props) {
  const { contextParams, page } = useCorePageState();

  const titleParts = typeof title === 'string' ? [title] : title;
  const { isMobile } = useMobile();

  const handleParamsChange = (updates: Params) => {
    const newFocusBrand = updates['moFocusBrand']?.[0];
    if (newFocusBrand && newFocusBrand !== contextParams['highlightedBrand'][0]) {
      // if moFocusBrand is changed, update the highlightedBrand
      ContextManager.getInstance().updateSettingsValues({
        ['highlightedBrand']: [{ label: newFocusBrand, value: newFocusBrand }],
      });
    }
    updateSettings({
      ...{},
      ...pageParams,
      ...updates,
    });
  };

  return (
    <div className={`text-xl text-heading flex gap-2 my-0 items-baseline ${isMobile ? 'mobile-wrap' : ''}`}>
      {titleParts.map((part, idx) => {
        const isPageOverview = page.name.includes('overview');
        const shouldApplyOverviewMobileTitle = isPageOverview && isMobile && idx >= 2;
        const isFirstTwoTitle = isMobile && idx < 2;

        const buttonComponent = (props: MenuButtonProps) => {
          return <TitleButton {...props} isOverviewMobile={shouldApplyOverviewMobileTitle} />;
        };

        return (
          <Fragment key={`pt_${idx}`}>
            {isPageOverview && isMobile && idx === 2 && <div className="line-break" />}
            {typeof part === 'string' && (
              <span
                className={classNames({
                  'font-bold': !isMobile,
                  'text-md': shouldApplyOverviewMobileTitle,
                  'overview-header-first-row': isFirstTwoTitle,
                })}
              >
                {part}
              </span>
            )}
            {typeof part !== 'string' && (
              <SelectionMenuComponent
                menuConfig={part.menu}
                values={pageParams || {}}
                onUpdate={handleParamsChange}
                button={buttonComponent}
                defaultValue={part.value}
                showGroupLabel={part.menu?.showGroupLabel}
                isSubMenuIndented={part.menu.paramName === 'moFocusBrand'}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function TitleButton({ onClick, children, isOverviewMobile }: MenuButtonProps & { isOverviewMobile: boolean }) {
  const { Icon } = elements;

  return (
    <button
      className={classNames('text-left text-brand p-0 flex items-center transition-colors hover:text-brand-dark', {
        'text-md': isOverviewMobile,
        'text-xl': !isOverviewMobile,
      })}
      onClick={onClick}
    >
      {children}
      <Icon color="brand">
        <Icon.icons.ChevronDown />
      </Icon>
    </button>
  );
}

export { PageTitle, TitleButton };
